import type { UserRole, UserRow } from '@lastrouter/contracts';
import {
  Alert,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  Field,
  focusRing,
  Input,
  NativeSelect,
  Search,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  Trash2,
  UserPlus,
} from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { API_BASE } from '../api/client.js';
import { errorMessage, useApiMutation, useApiQuery } from '../api/hooks.js';
import { AdminOnly } from '../app/shell.js';
import { EmptyState, PageHeader, QueryState } from '../components/common.js';
import { formatDateTime } from '../lib/format.js';
import { AccessPanel } from './access.js';
import { PerUserLimits } from './limits.js';

const ROLES: UserRole[] = ['reader', 'editor', 'admin'];
type RoleFilter = 'all' | UserRole | 'none';

/** Três botões, um por papel: o marcado é o papel atual. Mudar é um clique. */
function RolePicker({ value, onChange, disabled, label, lockAdmin }: { value: UserRole | null; onChange: (r: UserRole) => void; disabled: boolean; label: string; lockAdmin: boolean }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-[var(--lr-radius-md)] border border-border bg-sunken p-0.5">
      {ROLES.map((r) => {
        const on = value === r;
        const blocked = disabled || (lockAdmin && r !== 'admin');
        return (
          <button
            key={r}
            type="button"
            aria-pressed={on}
            disabled={blocked}
            onClick={() => !on && onChange(r)}
            className={cn(
              'rounded-[calc(var(--lr-radius-md)-2px)] px-2.5 py-1 text-[length:var(--lr-text-sm)] text-fg-secondary transition-colors enabled:hover:text-fg disabled:cursor-not-allowed disabled:opacity-50',
              on && 'bg-surface font-medium text-fg shadow-sm',
              focusRing,
            )}
          >
            {t(`users.roles.${r}`)}
          </button>
        );
      })}
    </div>
  );
}

function AddPersonDialog({ open, onOpenChange, initialQuery }: { open: boolean; onOpenChange: (v: boolean) => void; initialQuery: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState(initialQuery);
  const [term, setTerm] = useState(initialQuery.trim().length >= 2 ? initialQuery.trim() : '');
  const [role, setRole] = useState<UserRole>('reader');
  const search = useApiQuery('searchDirectory', { query: { q: term } }, { enabled: open && term.length >= 2 });
  const set = useApiMutation('setUserRole', ['users']);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (q.trim().length >= 2) setTerm(q.trim());
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('users.add.title')} description={t('users.add.help')} closeLabel={t('common.close')}>
        <div className="flex flex-col gap-4">
          <Field id="novo-papel" label={t('users.add.role')}>
            <NativeSelect value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`users.roles.${r}`)}: {t(`users.roleHelp.${r}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <form onSubmit={submit} className="flex items-end gap-2">
            <Field id="busca-diretorio" label={t('users.add.search')} className="flex-1">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('users.add.searchPlaceholder')} autoFocus />
            </Field>
            <Button type="submit" disabled={q.trim().length < 2}>
              <Search aria-hidden="true" />
              {t('users.add.searchButton')}
            </Button>
          </form>
          <div aria-live="polite">
            {term.length < 2 ? null : search.isPending ? (
              <p className="text-fg-secondary">{t('common.loading')}</p>
            ) : search.error ? (
              <Alert tone="danger" title={errorMessage(t, search.error)} />
            ) : search.data?.length ? (
              <ul aria-busy={search.isPlaceholderData} className={cn('flex flex-col divide-y divide-border rounded-[var(--lr-radius-md)] border border-border transition-opacity', search.isPlaceholderData && 'pointer-events-none opacity-55')}>
                {search.data.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">{p.name ?? p.login}</span>
                      <span className="truncate text-[length:var(--lr-text-sm)] text-fg-muted">{p.login}</span>
                    </span>
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={set.isPending}
                      onClick={() =>
                        set.mutate(
                          { params: { id: p.id }, body: { role } },
                          {
                            onSuccess: () => {
                              toast.success(t('users.add.done', { name: p.name ?? p.login, role: t(`users.roles.${role}`) }));
                              onOpenChange(false);
                            },
                            onError: (err) => toast.error(errorMessage(t, err)),
                          },
                        )
                      }
                    >
                      {t('users.add.give', { role: t(`users.roles.${role}`) })}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-fg-secondary">{t('users.add.noResults')}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RemoveDialog({ user, onClose }: { user: UserRow | null; onClose: () => void }) {
  const { t } = useTranslation();
  const set = useApiMutation('setUserRole', ['users']);
  const who = user?.name ?? user?.login ?? '';
  return (
    <Dialog open={user !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('users.remove.title', { name: who })} description={t('users.remove.help')} closeLabel={t('common.close')}>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            disabled={set.isPending}
            onClick={() =>
              set.mutate(
                { params: { id: user!.id! }, body: { role: null } },
                {
                  onSuccess: () => {
                    toast.success(t('users.remove.done', { name: who }));
                    onClose();
                  },
                  onError: (err) => toast.error(errorMessage(t, err)),
                },
              )
            }
          >
            <Trash2 aria-hidden="true" />
            {t('users.remove.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UsersTab() {
  const { t } = useTranslation();
  const q = useApiQuery('users');
  const set = useApiMutation('setUserRole', ['users']);
  const [text, setText] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<UserRow | null>(null);
  const canEdit = q.data?.graph.state === 'ok';

  function changeRole(u: UserRow, role: UserRole) {
    set.mutate(
      { params: { id: u.id! }, body: { role } },
      {
        onSuccess: () => toast.success(t('users.changed', { name: u.name ?? u.login, role: t(`users.roles.${role}`) })),
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  }

  /** O conteúdo de cada coluna, igual na tabela (computador) e no cartão (celular). */
  function cells(u: UserRow, selfId: string | null) {
    const isSelf = u.id === selfId;
    const who = u.name ?? u.login ?? u.id ?? '';
    return {
      person: (
        <div className="flex min-w-0 flex-col">
          <span className="break-words font-medium">
            {who}
            {isSelf ? <span className="ml-1 text-fg-muted">({t('users.you')})</span> : null}
          </span>
          {u.login && u.name ? <span className="break-all text-[length:var(--lr-text-sm)] text-fg-muted">{u.login}</span> : null}
          <span className="mt-1 flex flex-wrap gap-1">
            {u.kind === 'group' ? <Badge>{t('users.group')}</Badge> : null}
            <Badge tone={u.origin === 'entra' ? 'success' : 'neutral'}>{t(`users.origin.${u.origin}`)}</Badge>
          </span>
        </div>
      ),
      roles: u.roles.length ? (
        <span className="flex flex-wrap gap-1">
          {u.roles.map((r) => (
            <Badge key={r} tone={r === 'admin' ? 'warning' : 'accent'}>
              {t(`users.roles.${r}`)}
            </Badge>
          ))}
        </span>
      ) : (
        <span className="text-fg-muted">{t('users.noRole')}</span>
      ),
      qlik: u.source_users.length
        ? u.source_users.map((s) => (
            <div key={s.source_id} title={s.user ? s.source_id : s.detail}>
              {s.user ?? '-'}
            </div>
          ))
        : '-',
      lastSeen: u.last_seen_at ? formatDateTime(u.last_seen_at) : t('users.never'),
      actions: u.id ? (
        <div className="flex flex-wrap items-center gap-2">
          <RolePicker
            value={u.roles.length === 1 ? u.roles[0]! : null}
            label={t('users.roleOf', { name: who })}
            disabled={!canEdit || set.isPending}
            lockAdmin={isSelf}
            onChange={(r) => changeRole(u, r)}
          />
          <Tooltip content={isSelf ? t('users.selfLock') : t('users.remove.button')}>
            <span>
              <Button size="sm" variant="ghost" aria-label={t('users.remove.aria', { name: who })} disabled={!canEdit || isSelf} onClick={() => setRemoving(u)}>
                <Trash2 aria-hidden="true" />
                {t('users.remove.button')}
              </Button>
            </span>
          </Tooltip>
        </div>
      ) : (
        <Button size="sm" variant="primary" disabled={!canEdit} onClick={() => setAdding(u.login ?? '')}>
          <UserPlus aria-hidden="true" />
          {t('users.giveAccess')}
        </Button>
      ),
    };
  }

  return (
    <>
      <QueryState query={q}>
        {(data) => {
          const needle = text.trim().toLowerCase();
          const rows = data.users.filter(
            (u) =>
              (!needle || `${u.name ?? ''} ${u.login ?? ''}`.toLowerCase().includes(needle)) &&
              (roleFilter === 'all' || (roleFilter === 'none' ? u.roles.length === 0 : u.roles.includes(roleFilter))),
          );
          return (
            <div className="flex flex-col gap-4">
              {data.graph.state === 'ok' ? null : (
                <Alert tone={data.graph.state === 'forbidden' ? 'warning' : data.graph.state === 'error' ? 'danger' : 'info'} title={t(`users.graph.${data.graph.state}`)}>
                  <p>{data.graph.message}</p>
                  {data.graph.state === 'forbidden' || data.graph.state === 'not_connected' ? <p className="mt-1">{t('users.graph.steps')}</p> : null}
                  {data.graph.state === 'forbidden' || data.graph.state === 'not_connected' ? (
                    <Button variant="secondary" className="mt-2" asChild>
                      {/* Navegação real: o serviço pede ao Entra o token delegado do admin e volta para esta tela. */}
                      <a href={`${API_BASE}/auth/graph?next=/users`}>{t('users.graph.connect')}</a>
                    </Button>
                  ) : null}
                </Alert>
              )}
              <p className="text-fg-secondary">{t('users.subtitle')}</p>
              <div className="flex flex-wrap items-end gap-3">
                <Field id="busca-usuarios" label={t('users.search')} className="min-w-60 flex-1">
                  <Input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder={t('users.searchPlaceholder')} />
                </Field>
                <Field id="filtro-papel" label={t('users.roleFilter')}>
                  <NativeSelect value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}>
                    <option value="all">{t('common.all')}</option>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {t(`users.roles.${r}`)}
                      </option>
                    ))}
                    <option value="none">{t('users.noRole')}</option>
                  </NativeSelect>
                </Field>
                <Button variant="primary" className="ml-auto" onClick={() => setAdding('')} disabled={!canEdit}>
                  <UserPlus aria-hidden="true" />
                  {t('users.addButton')}
                </Button>
              </div>
              {rows.length === 0 ? (
                <EmptyState>{data.users.length ? t('users.noMatch') : t('users.empty')}</EmptyState>
              ) : (
                <>
                  {/* Celular e tablet: um cartão por pessoa, sem rolagem para o lado. */}
                  <ul aria-label={t('users.caption', { count: rows.length })} className="flex flex-col gap-3 lg:hidden">
                    {rows.map((u) => {
                      const c = cells(u, data.self_id);
                      return (
                        <li key={u.id ?? u.login} className="flex flex-col gap-3 rounded-[var(--card-radius)] border border-border bg-surface p-4">
                          {c.person}
                          <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 text-[length:var(--lr-text-sm)]">
                            <dt className="text-fg-secondary">{t('users.role')}</dt>
                            <dd className="min-w-0">{c.roles}</dd>
                            <dt className="text-fg-secondary">{t('users.qlikUser')}</dt>
                            <dd className="min-w-0 break-all font-mono">{c.qlik}</dd>
                            <dt className="text-fg-secondary">{t('users.lastSeen')}</dt>
                            <dd className="min-w-0">{c.lastSeen}</dd>
                          </dl>
                          <div className="border-t border-border pt-3">{c.actions}</div>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="hidden lg:block">
                    <Table>
                      <TableCaption>{t('users.caption', { count: rows.length })}</TableCaption>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('users.person')}</TableHead>
                          <TableHead>{t('users.role')}</TableHead>
                          <TableHead>{t('users.qlikUser')}</TableHead>
                          <TableHead>{t('users.lastSeen')}</TableHead>
                          <TableHead>{t('common.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((u) => {
                          const c = cells(u, data.self_id);
                          return (
                            <TableRow key={u.id ?? u.login}>
                              <TableCell>{c.person}</TableCell>
                              <TableCell>{c.roles}</TableCell>
                              <TableCell className="font-mono text-[length:var(--lr-text-sm)]">{c.qlik}</TableCell>
                              <TableCell className="whitespace-nowrap text-[length:var(--lr-text-sm)]">{c.lastSeen}</TableCell>
                              <TableCell>{c.actions}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          );
        }}
      </QueryState>
      {adding !== null ? <AddPersonDialog open onOpenChange={(v) => !v && setAdding(null)} initialQuery={adding} /> : null}
      <RemoveDialog user={removing} onClose={() => setRemoving(null)} />
    </>
  );
}

export type PeopleTab = 'users' | 'limits' | 'simulate';

/** Pessoas: quem tem acesso e com qual papel, as exceções de limite e a simulação de acesso. */
export function UsersPage({ tab, onTab }: { tab: PeopleTab; onTab: (t: PeopleTab) => void }) {
  const { t } = useTranslation();
  return (
    <AdminOnly>
      <PageHeader title={t('people.title')} subtitle={t('people.subtitle')} />
      <Tabs value={tab} onValueChange={(v) => onTab(v as PeopleTab)}>
        <TabsList aria-label={t('people.title')}>
          <TabsTrigger value="users">{t('people.tabUsers')}</TabsTrigger>
          <TabsTrigger value="limits">{t('people.tabLimits')}</TabsTrigger>
          <TabsTrigger value="simulate">{t('people.tabSimulate')}</TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <UsersTab />
        </TabsContent>
        <TabsContent value="limits">
          <PerUserLimits />
        </TabsContent>
        <TabsContent value="simulate">
          <AccessPanel />
        </TabsContent>
      </Tabs>
    </AdminOnly>
  );
}
